import type { EventForm } from '@/components/FuelCalendarContainer';
import { EventForm as EventFormClass, RruleOptions } from '@/components/FuelCalendarContainer/EventForm';
import type { Broadcast, BroadcastRecurrence, BroadcastSchedule, BroadcastStartStopMethod } from '@/models/broadcast';
import type { LiveProgramScheduleDisplay, LiveProgramScheduleDisplayStatus } from '@/models/channel';
import type { Input } from '@/models/input';
import { FuelEntityType, FuelPortalService } from '@/services';
import calendarUtils from '@/utils/calendarUtils';
import clientUtils from '@/utils/clientUtils';
import { compressionUtils } from '@/utils/compressionUtils';
import { DateTime } from 'luxon';
import { Frequency, RRule } from 'rrule';
import { v4 as uuid } from 'uuid';

import { getScheduleDatePart, parseScheduleInTimezone } from './broadcastScheduleDateUtils';

export const INPUT_SCHEDULE_DAYS_TO_KEEP = 8;

/**
 * Fetch a single input by id to obtain its `scheduleICSData`.
 *
 * The Input LIST endpoint does not return `scheduleICSData`; only the
 * individual entity fetch includes it.
 */
export const getInputByIdWithSchedule = async (id: string | undefined): Promise<Input | null> => {
  if (!id) return null;
  try {
    const fetched = await FuelPortalService.getEntityById<Input>(FuelEntityType.Input, id);
    return fetched && typeof fetched === 'object' && 'id' in fetched && fetched.id ? fetched : null;
  } catch {
    return null;
  }
};

/** Decode and parse an input source's embedded ICS schedule into calendar events. */
export const decodeInputScheduleEvents = (
  input: Input | undefined,
  timezone: string,
  daysToKeep: number = INPUT_SCHEDULE_DAYS_TO_KEEP,
): EventForm[] => {
  if (!input?.scheduleICSData) return [];
  const decoded = calendarUtils.decodeICalendarConfig(input.scheduleICSData, false);
  if (!decoded) return [];
  return calendarUtils.getEventsFromICS(decoded, timezone, daysToKeep);
};

/** Decode all ICS events without the display-window age filter. */
export const decodeAllInputScheduleEvents = (input: Input | undefined, timezone: string): EventForm[] =>
  decodeInputScheduleEvents(input, timezone, -1);

/** Encode calendar events into base64/compressed `scheduleICSData` (mirrors InputScheduling). */
export const encodeScheduleEventsToIcsData = (events: EventForm[], timezone: string): string | null => {
  if (events.length === 0) return null;

  const icsString = calendarUtils.getICSString(events, timezone);
  if (!icsString) return null;

  if (icsString.length > 200_000) {
    return compressionUtils.compress(icsString);
  }
  return compressionUtils.base64Encode(icsString);
};

const BROADCAST_DAY_TO_RRULE: Record<string, typeof RRule.MO> = {
  Sun: RRule.SU,
  Mon: RRule.MO,
  Tue: RRule.TU,
  Wed: RRule.WE,
  Thu: RRule.TH,
  Fri: RRule.FR,
  Sat: RRule.SA,
};

const applyBroadcastRecurrenceToEvent = (
  form: EventFormClass,
  schedule: BroadcastSchedule,
  timezone: string,
): void => {
  const recurrence = schedule.recurrence;
  if (!recurrence || recurrence.type === 'None') {
    form.recurrenceRule = '';
    return;
  }

  const rruleOptions = new RruleOptions();
  rruleOptions.dtstart = new Date(form.start as string);
  rruleOptions.interval = recurrence.interval || 1;
  rruleOptions.wkst = RRule.SU;

  switch (recurrence.type) {
    case 'Daily':
      rruleOptions.freq = Frequency.DAILY;
      break;
    case 'Weekly':
      rruleOptions.freq = Frequency.WEEKLY;
      rruleOptions.byweekday = recurrence.days
        .map((day) => BROADCAST_DAY_TO_RRULE[day])
        .filter(Boolean);
      break;
    case 'Monthly':
      rruleOptions.freq = Frequency.MONTHLY;
      break;
    case 'Yearly':
      rruleOptions.freq = Frequency.YEARLY;
      break;
    default:
      break;
  }

  if (recurrence.end === 'OnDate' && recurrence.endDate) {
    rruleOptions.until = DateTime.fromISO(recurrence.endDate, { zone: timezone }).endOf('day').toJSDate();
  } else if (recurrence.end === 'AfterOccurrences' && recurrence.occurrences) {
    rruleOptions.count = recurrence.occurrences;
  }

  form.rrule = rruleOptions;
  const rruleBody = calendarUtils.extractRRuleBody(new RRule(rruleOptions).toString());
  form.recurrenceRule = rruleBody;
};

/** Build a calendar event from a broadcast schedule for embedding in input ICS. */
export const buildEventFormFromBroadcastSchedule = (
  broadcast: Broadcast,
  liveProgramId: string,
  timezone: string,
): EventForm | null => {
  const schedule = broadcast.schedule;
  if (!schedule?.startDate || !schedule.startTime || !schedule.duration || !timezone?.trim()) {
    return null;
  }

  const datePart = getScheduleDatePart(schedule.startDate, timezone);
  if (!datePart) return null;

  const start = DateTime.fromISO(`${datePart}T${schedule.startTime}`, { zone: timezone });
  if (!start.isValid) return null;

  const end = start.plus({ minutes: schedule.duration });
  const form = EventFormClass.create(start.toJSDate(), end.toJSDate(), timezone, false, 'Input');
  form.id = uuid();
  form.title = broadcast.title;
  form.start = start.toISO();
  form.end = end.toISO();
  form.duration = calendarUtils.getDuration(form.start, form.end);
  form.attachedFuelEntityType = FuelEntityType.LiveProgram;
  form.attachedFuelEntityId = `type=${FuelEntityType.LiveProgram};id=${liveProgramId}`;
  form.attachedFuelDisplayClassName = FuelEntityType.LiveProgram;
  form.color = '#ff4147';
  applyBroadcastRecurrenceToEvent(form, schedule, timezone);
  return form;
};

const persistInputScheduleEvents = async (
  input: Input,
  events: EventForm[],
  timezone: string,
): Promise<Input | null> => {
  const scheduleICSData = encodeScheduleEventsToIcsData(events, timezone);
  try {
    const result = await FuelPortalService.upsertEntity<Input>(FuelEntityType.Input, {
      ...input,
      scheduleICSData,
    });
    if (!result || typeof result !== 'object' || !('id' in result) || !result.id) {
      return null;
    }
    return {
      ...input,
      ...result,
      scheduleICSData,
    };
  } catch {
    return null;
  }
};

/** Remove a live program booking from an input source ICS schedule. */
export const removeLiveProgramFromInputSchedule = async (
  inputId: string,
  liveProgramId: string,
  timezone: string,
): Promise<void> => {
  const input = await getInputByIdWithSchedule(inputId);
  if (!input?.id) return;

  const events = calendarUtils.removeEPGEventsByLiveProgramId(
    decodeAllInputScheduleEvents(input, timezone),
    liveProgramId,
  );
  await persistInputScheduleEvents(input, events, timezone);
};

/**
 * Sync a broadcast's schedule onto an input source ICS (add, update, or remove booking).
 */
export const syncInputScheduleForBroadcast = async (
  inputId: string,
  broadcast: Broadcast,
  liveProgramId: string,
  timezone: string,
  startStopMethod: BroadcastStartStopMethod,
): Promise<void> => {
  const input = await getInputByIdWithSchedule(inputId);
  if (!input?.id) return;

  let events = calendarUtils.removeEPGEventsByLiveProgramId(
    decodeAllInputScheduleEvents(input, timezone),
    liveProgramId,
  );

  if (startStopMethod !== 'Manual') {
    const event = buildEventFormFromBroadcastSchedule(broadcast, liveProgramId, timezone);
    if (event) {
      events.push(event);
    }
  }

  await persistInputScheduleEvents(input, events, timezone);
};

const DAY_TO_BROADCAST_DAY: Record<string, string> = {
  SU: 'Sun',
  MO: 'Mon',
  TU: 'Tue',
  WE: 'Wed',
  TH: 'Thu',
  FR: 'Fri',
  SA: 'Sat',
};

const createDefaultRecurrence = (): BroadcastRecurrence => ({
  type: 'None',
  interval: 1,
  days: [],
  end: 'Never',
  endDate: null,
  occurrences: null,
});

const parseRRuleParts = (recurrenceRule: string | null | undefined): Record<string, string> => {
  if (!recurrenceRule) return {};
  const value = recurrenceRule.replace(/^RRULE:/i, '');
  return value.split(';').reduce<Record<string, string>>((acc, part) => {
    const [k, v] = part.split('=');
    if (k && v) {
      acc[k.toUpperCase()] = v;
    }
    return acc;
  }, {});
};

const parseUntilDate = (rawUntil: string | undefined, timezone: string): string | null => {
  if (!rawUntil) return null;
  const utc = DateTime.fromFormat(rawUntil, "yyyyMMdd'T'HHmmss'Z'", { zone: 'utc' });
  if (utc.isValid) return utc.setZone(timezone).toISODate();

  const local = DateTime.fromFormat(rawUntil, "yyyyMMdd'T'HHmmss", { zone: timezone });
  if (local.isValid) return local.toISODate();

  const dateOnly = DateTime.fromFormat(rawUntil, 'yyyyMMdd', { zone: timezone });
  return dateOnly.isValid ? dateOnly.toISODate() : null;
};

const buildRecurrenceFromRRule = (recurrenceRule: string | null | undefined, timezone: string): BroadcastRecurrence => {
  const recurrence = createDefaultRecurrence();
  const parts = parseRRuleParts(recurrenceRule);

  const freq = (parts.FREQ || 'NONE').toUpperCase();
  if (freq === 'DAILY') recurrence.type = 'Daily';
  else if (freq === 'WEEKLY') recurrence.type = 'Weekly';
  else if (freq === 'MONTHLY') recurrence.type = 'Monthly';
  else if (freq === 'YEARLY') recurrence.type = 'Yearly';

  const interval = Number(parts.INTERVAL);
  recurrence.interval = Number.isFinite(interval) && interval > 0 ? interval : 1;

  if (parts.BYDAY) {
    recurrence.days = parts.BYDAY.split(',').map((day) => DAY_TO_BROADCAST_DAY[day]).filter(Boolean);
  }

  const count = Number(parts.COUNT);
  if (Number.isFinite(count) && count > 0) {
    recurrence.end = 'AfterOccurrences';
    recurrence.occurrences = count;
  } else {
    const endDate = parseUntilDate(parts.UNTIL, timezone);
    if (endDate) {
      recurrence.end = 'OnDate';
      recurrence.endDate = endDate;
    }
  }

  return recurrence;
};

/**
 * Maps a decoded ICS schedule event into the broadcast form schedule shape.
 */
export const mapScheduleEventToBroadcastSchedule = (
  event: EventForm,
  timezone: string,
): BroadcastSchedule | null => {
  if (!event?.start || !event?.end) return null;

  const start = parseScheduleInTimezone(event.start, timezone);
  const end = parseScheduleInTimezone(event.end, timezone);
  if (!start?.isValid || !end?.isValid) return null;

  const duration = Math.max(1, Math.round(end.diff(start, 'minutes').minutes));
  return {
    enabled: true,
    startDate: start.toISODate(),
    startTime: start.toFormat('HH:mm'),
    endTime: end.toFormat('HH:mm'),
    duration,
    recurrence: buildRecurrenceFromRRule(event.recurrenceRule, timezone),
  };
};

/**
 * Maps the input source's ICS booking for a specific live program into the
 * broadcast form schedule shape.
 *
 * Used when editing an existing program: the program's own booking lives in the
 * input ICS, so the schedule fields are hydrated from the event whose
 * `attachedFuelEntityId` matches `liveProgramId`. Decodes without the display
 * age window so a past one-time booking still hydrates. Returns `null` when the
 * program has no booking on the input (leaving any saved schedule intact).
 */
export const getBroadcastScheduleFromInputForProgram = (
  input: Input | undefined,
  timezone: string,
  liveProgramId: string | null | undefined,
): BroadcastSchedule | null => {
  if (!liveProgramId) return null;

  const events = decodeAllInputScheduleEvents(input, timezone).filter((event) =>
    calendarUtils.eventMatchesLiveProgram(event, liveProgramId),
  );
  if (events.length === 0) return null;

  const firstEvent = [...events].sort((a, b) => {
    const aStart = a.start ? new Date(a.start).getTime() : 0;
    const bStart = b.start ? new Date(b.start).getTime() : 0;
    return aStart - bStart;
  })[0];

  return mapScheduleEventToBroadcastSchedule(firstEvent, timezone);
};

/** Build an ISO datetime from a schedule date (`yyyy-MM-dd`) + time (`HH:mm`) in the OU timezone. */
const buildScheduleDateTimeIso = (
  startDate: string | null | undefined,
  time: string | null | undefined,
  timezone: string,
): string | undefined => {
  if (!startDate || !time || !timezone?.trim()) return undefined;
  const datePart = getScheduleDatePart(startDate, timezone);
  if (!datePart) return undefined;
  const dateTime = DateTime.fromISO(`${datePart}T${time}`, { zone: timezone });
  return dateTime.isValid ? (dateTime.toISO() ?? undefined) : undefined;
};

/** Human-readable recurrence label mirroring how the broadcast list renders `scheduleDisplayRecurrence`. */
const describeBroadcastRecurrence = (recurrence: BroadcastRecurrence | undefined): string | undefined => {
  if (!recurrence || recurrence.type === 'None') return undefined;

  switch (recurrence.type) {
    case 'Daily':
      return 'Daily';
    case 'Weekly': {
      const days = recurrence.days ?? [];
      const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
      const isWeekdays = days.length === weekdays.length && weekdays.every((day) => days.includes(day));
      if (isWeekdays) return 'Mon\u2013Fri';
      if (days.length) return `Weekly \u00b7 ${days.join(', ')}`;
      return 'Weekly';
    }
    case 'Monthly':
      return 'Monthly';
    case 'Yearly':
      return 'Yearly';
    default:
      return undefined;
  }
};

/**
 * Maps a broadcast form schedule (pulled in-memory from the selected input source) into the
 * `LiveProgram.metadata.scheduleDisplay` shape persisted on the live program.
 *
 * Mirrors `LiveProgramDto.ScheduleDisplay` minus `index` (index ordering is owned elsewhere).
 * `Live` is set at read time from input state, so only `Scheduled`/`NotScheduled` are persisted.
 */
export const buildScheduleDisplayFromBroadcastSchedule = (
  schedule: BroadcastSchedule | undefined,
  timezone: string,
  startStopMethod?: BroadcastStartStopMethod,
): LiveProgramScheduleDisplay | undefined => {
  if (!schedule) return undefined;

  if (startStopMethod === 'Manual') {
    return {
      status: 'NotScheduled',
      startTime: null,
      endTime: null,
      recurrence: null,
    };
  }

  const startTime = buildScheduleDateTimeIso(schedule.startDate, schedule.startTime, timezone);

  let endTime = buildScheduleDateTimeIso(schedule.startDate, schedule.endTime, timezone);
  if (startTime && endTime && DateTime.fromISO(endTime) <= DateTime.fromISO(startTime)) {
    // End wraps past midnight relative to start; push it to the next day.
    endTime = DateTime.fromISO(endTime).plus({ days: 1 }).toISO() ?? endTime;
  }
  if (startTime && !endTime && schedule.duration) {
    endTime = DateTime.fromISO(startTime).plus({ minutes: schedule.duration }).toISO() ?? undefined;
  }

  const recurrence = describeBroadcastRecurrence(schedule.recurrence);
  const status: LiveProgramScheduleDisplayStatus = startTime ? 'Scheduled' : 'NotScheduled';

  const scheduleDisplay: LiveProgramScheduleDisplay = { status };
  if (startTime) scheduleDisplay.startTime = startTime;
  if (endTime) scheduleDisplay.endTime = endTime;
  if (recurrence) scheduleDisplay.recurrence = recurrence;

  return scheduleDisplay;
};
